import Link from 'next/link'

import { ACADEMY_BASE_PATH } from '@/utilities/academyBasePath'
import { cn } from '@/utilities/ui'
import { formatDifficultyLabel, type AcademyLessonSummary } from '@/utilities/academyCourseMeta'
import { AcademyCourseIllustration } from './AcademyCourseIllustration'

interface Props {
  lesson: AcademyLessonSummary
  /** Zero-based position in the course, used for the "Lesson 0X" label. */
  index: number
  courseSlug: string
  difficulty?: string | null
  /** Per-course illustration slug. */
  illustration?: string | null
  className?: string
}

/**
 * The swappable lesson preview panel (Figma 1164-2970): meta line, title,
 * summary, "Start lesson" CTA and the faded course illustration.
 */
export function LessonContentPanel({
  lesson,
  index,
  courseSlug,
  difficulty,
  illustration,
  className,
}: Props) {
  const lessonNumber = String(index + 1).padStart(2, '0')

  const metaParts = [`Lesson ${lessonNumber}`]
  if (lesson.readingTimeMinutes) metaParts.push(`${lesson.readingTimeMinutes} min`)
  const difficultyLabel = formatDifficultyLabel(difficulty)
  if (difficultyLabel) metaParts.push(difficultyLabel)

  return (
    <div
      className={cn(
        // Single column on mobile (no illustration); content + 200px illustration column on tablet/desktop.
        'grid grid-cols-1 gap-6 overflow-hidden rounded-[20px] bg-civo-cloud p-5 md:grid-cols-[minmax(0,1fr)_200px] md:gap-8 md:p-8 lg:p-12',
        className,
      )}
    >
      <div className="flex flex-col gap-6">
        <p className="body-small mb-0 text-black/70">{metaParts.join('  ·  ')}</p>

        <h3 className="display-medium mb-0">{lesson.title}</h3>

        {lesson.snippet && <p className="body-medium mb-0 text-black/70">{lesson.snippet}</p>}

        <Link
          href={`${ACADEMY_BASE_PATH}/${courseSlug}/${lesson.slug}`}
          aria-label={`Start lesson: ${lesson.title}`}
          className="group inline-flex items-center gap-4 self-start rounded-full bg-black py-0.5 pl-6 pr-0.5 transition-colors hover:bg-black/90"
        >
          <span className="subheading-xsmall text-white">Start lesson</span>
          <span className="flex size-10 items-center justify-center rounded-full bg-civo-lime transition-transform duration-300 group-hover:scale-105">
            <svg
              width="12"
              height="12"
              viewBox="0 0 8 8"
              fill="none"
              aria-hidden="true"
              className="size-3"
            >
              <path
                d="M4 8L3.2875 7.3L6.0875 4.5H0V3.5H6.0875L3.2875 0.7L4 0L8 4L4 8Z"
                fill="currentColor"
              />
            </svg>
          </span>
        </Link>
      </div>

      {/* Course illustration — hidden on mobile (matches Figma); bottom-right on tablet + desktop. */}
      <div className="hidden md:flex md:items-end md:justify-end">
        <AcademyCourseIllustration illustration={illustration} className="size-[200px] object-contain" />
      </div>
    </div>
  )
}
